import {
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import type { Translations } from "./locales/sl";
import { localeFromPath } from "./routes";
import { translations } from "./translations";
import { LOCALES, type Locale } from "./types";

interface LocaleContextValue {
  locale: Locale;
  t: Translations;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

type LocaleProviderProps = {
  children: React.ReactNode;
  /** SSR: request path used before the router location is available */
  initialPath?: string;
};

export function LocaleProvider({ children, initialPath }: LocaleProviderProps) {
  const location = useLocation();
  const pathname =
    location.pathname ||
    initialPath ||
    (typeof window !== "undefined" ? window.location.pathname : "/");

  // Shared reports have one token route across languages. Resolve their query
  // locale here so the report, footer and document language all agree.
  const sharedLocale = /^\/r\/[^/]+\/?$/.test(pathname)
    ? new URLSearchParams(location.search).get("lang")
    : null;
  const locale = sharedLocale && LOCALES.includes(sharedLocale as Locale)
    ? sharedLocale as Locale
    : localeFromPath(pathname);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      t: translations[locale],
    }),
    [locale]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}

export function interpolate(
  template: string,
  vars: Record<string, string | number>
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    String(vars[key] ?? `{{${key}}}`)
  );
}

export function formatCredits(count: number, locale: Locale): string {
  if (locale === "en") {
    return `${count} credit${count === 1 ? "" : "s"}`;
  }
  if (locale === "de") {
    return `${count} Credit${count === 1 ? "" : "s"}`;
  }
  if (locale === "ru") {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} кредит`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      return `${count} кредита`;
    }
    return `${count} кредитов`;
  }
  if (count === 1) return "1 kredit";
  if (count === 2) return "2 kredita";
  if (count === 3 || count === 4) return `${count} krediti`;
  return `${count} kreditov`;
}

export function dateLocale(locale: Locale): string {
  if (locale === "en") return "en-GB";
  if (locale === "de") return "de-DE";
  if (locale === "ru") return "ru-RU";
  return "sl-SI";
}
